import { createAsyncThunk } from '@reduxjs/toolkit';

import { getCookie, setCookie } from 'helpers/cookie';
import { getTranslationKey } from 'helpers/texting';
import { getEndCustomer } from 'old-store/actions';
import { RootState } from 'old-store/store';
import { ICollectionIdentity, IImage } from 'old-store/types/gallery';
import { Api } from 'old-store/utils';
import { showErrorNotify } from 'modules/ScrPhotobook/store/slices/photobook/actions';
import {
  setCollectionIdentities,
  setExistingImages,
  setImageIdsForIdentity,
  IdentitiesGroupedByIdI
} from 'store/slices/images';
import { toggleInfoModal } from 'store/slices/modals';
import {
  checkIfAuthorizedAndLogoutIfNot,
  fetchCustomerToken,
  getCustomerSelectionId
} from 'store/slices/selection/actions';

export const fetchCollectionIdentities = createAsyncThunk<void, string, { state: RootState }>(
  'images/fetchCollectionIdentities',
  async (collectionId, { dispatch }) => {
    try {
      const response = await Api.Images.getCollectionIdentities(collectionId);
      const groupedByIdIdentities: IdentitiesGroupedByIdI = {};

      response?.result?.list.forEach((item: ICollectionIdentity) => {
        groupedByIdIdentities[item._id] = item;
      });

      dispatch(
        setCollectionIdentities({
          collectionIdentities: response?.result?.list || [],
          groupedByIdIdentities,
          total: response?.result?.meta?.totalAmount || 0
        })
      );
    } catch (e) {
      console.log(e);
      showErrorNotify();
    }
  }
);

// The API endpoint unions image ids over identities, so combining faces (all-of)
// needs one result set per identity; already-fetched identities are skipped.
export const fetchImageIdsByIdentities = createAsyncThunk<void, string[], { state: RootState }>(
  'images/fetchImageIdsByIdentities',
  async (identityIds, { getState, dispatch }) => {
    const { imageIdsByIdentity } = getState().images.collectionIdentities;
    const identitiesToFetch = identityIds.filter((id) => !imageIdsByIdentity[id]);

    if (!identitiesToFetch.length) return;

    try {
      await Promise.all(
        identitiesToFetch.map(async (identityId) => {
          const response = await Api.Images.getImageIdsByIdentities([identityId]);
          if (response?.code) {
            throw new Error(response?.response?.data?.error);
          }

          dispatch(setImageIdsForIdentity({ identityId, imageIds: response?.result || [] }));
        })
      );
    } catch (e) {
      console.log(e);
      showErrorNotify();
    }
  }
);

export const commentImage = createAsyncThunk<Promise<any>, any, { state: RootState }>(
  'images/commentImage',
  async ({ imageId, comment }: { imageId: string; comment: string }, { getState, dispatch }) => {
    try {
      const { selection, collection } = getState();
      await fetchCustomerToken(collection._user);

      const customerSelectionId = await getCustomerSelectionId(
        selection,
        dispatch,
        collection._user
      );

      const commentData = {
        image_id: imageId,
        selection_id: customerSelectionId,
        collection_id: window.SITE_ID,
        comment
      };

      const { result } = await Api.TempCustomer.comment(commentData);
      dispatch(getEndCustomer());

      return result;
    } catch (e) {
      console.log('e', e);
    }
  }
);

export const hideImage = createAsyncThunk<
  Promise<boolean | undefined>,
  string,
  { state: RootState }
>('images/hideImage', async (imageId, { getState, dispatch }) => {
  try {
    const { collection, images } = getState();
    let { endCustomer } = getState();

    await fetchCustomerToken(collection._user);

    if (!endCustomer._id) {
      endCustomer = (await (dispatch as any)(getEndCustomer())) || endCustomer;
      /*
       * This handles a dirty cookie state. We assume the request inside
       * getAsyncEndCustomer threw a forbidden error and try recover into a valid state
       */
      if (!endCustomer) {
        checkIfAuthorizedAndLogoutIfNot(403, collection._user);

        return;
      }
    }

    const newImages = JSON.parse(JSON.stringify(images.images));
    const currentImage = newImages.find((image: IImage) => image._id === imageId);

    const imageHidden = currentImage.hiddenBy.includes(endCustomer._id);

    if (imageHidden) {
      currentImage.hiddenBy = currentImage.hiddenBy.filter(
        (item: string) => item !== endCustomer._id
      );

      dispatch(setExistingImages(newImages));

      await Api.Images.unHideImage(imageId);
    } else {
      currentImage.hiddenBy.push(endCustomer._id);

      dispatch(setExistingImages(newImages));

      await Api.Images.hideImage(imageId);
    }

    if (!imageHidden && !getCookie(`showHideImageHelper-${collection._id}`)) {
      dispatch(
        toggleInfoModal({
          title: getTranslationKey('firstTimeModal.title'),
          text1: getTranslationKey('firstTimeModal.text')
        })
      );

      setCookie(`showHideImageHelper-${collection._id}`, '1', 30);
    }

    // set images to correct optimistic update in case errors happened
    // const fetchedImages = await Api.Images.getImages();
    // dispatch(setImages({ images: fetchedImages, signedWildCardUrl: collection.signedWildCardUrl }));

    return imageHidden;
  } catch (e) {
    console.log('e', e);
  }
});
